/*
 * Copyright (c) Funtip GbR 2017.
 * Simon Oldeboershuis, Perry Prust
 */

import { all, put, select, takeEvery } from 'redux-saga/effects';
import { Action } from 'typescript-fsa';
import { userGroupHighscorePath } from '.';
import { loadWithoutPropsTournamentRoundForwarder } from '../../reactUtils';
import { UserGroupDefaultHighscoreRound } from '../../services/betting';
import { fetchWrapperFuncNoLoading } from '../../utils';
import { highscoresPath } from '../Highscores';
import { getGroupHighscoreApi2, getSystemConfig } from '../System/systemConfig';
import {
    fetchGroupHighscore,
    FETCH_GROUP_HIGHSCORES,
    GroupHighscorePagingValues,
    groupHighscoresSetDefaultRound,
    groupHighscoresSetIsLoading,
    GROUP_HIGHSCORES_LOAD_DEFAULT_ROUND,
    GROUP_HIGHSCORES_LOAD_WITHOUT_PROPS,
    HIGHSCORES_LOAD_WITHOUT_PROPS,
    PAGE_DOWN_GROUP_HIGHSCORES,
    PAGE_FIRST_GROUP_HIGHSCORES,
    PAGE_LAST_GROUP_HIGHSCORES,
    PAGE_UP_GROUP_HIGHSCORES,
    SEARCH_GROUP_HIGHSCORES,
    setGroupHighscores,
    setGroupHighscoreSearchString,
    setGroupHighscoresPagingValues,
} from './actions';
import { GroupHighscoreStore } from './reducer';
import {
    userGroupHighscoresDefaultTrSelector,
    userGroupHighscoresSelector,
    userGroupHighscoresTrIdSelector,
} from './selectors';

const getLastPageStart = (count: number, slotSize: number) => {
    return Math.floor(count / slotSize) * slotSize;
};

function* userGroupLoadDefaultHighscoreRoundGenerator(action: Action<string>) {
    const ugId = action.payload;
    const api = getGroupHighscoreApi2();

    const defaultRounds: { [ugId: string]: UserGroupDefaultHighscoreRound } = yield select(
        userGroupHighscoresDefaultTrSelector,
    );

    if (!defaultRounds[ugId]) {
        yield* fetchWrapperFuncNoLoading(
            () => api.getDefaultHighscoreRound({ ugId }),
            [(r) => groupHighscoresSetDefaultRound({ ugId, tr: r })],
        );
    }
}

export function* searchGroupHighscoreGenerator(action: Action<string>) {
    const groupHighscoreApi = getGroupHighscoreApi2();
    let searchString = action.payload;
    yield put(setGroupHighscoreSearchString(searchString));

    let ghsState: GroupHighscoreStore = yield select(userGroupHighscoresSelector);

    const trId: string | undefined = yield select(userGroupHighscoresTrIdSelector);
    if (trId) {
        yield put(groupHighscoresSetIsLoading(true));
        try {
            let count = yield* fetchWrapperFuncNoLoading(() =>
                groupHighscoreApi.getGroupHighscoreCount({
                    clientName: getSystemConfig().clientName,
                    trId: Number(trId),
                    q: searchString,
                }),
            );

            if (count !== undefined) {
                let params2: GroupHighscorePagingValues = {
                    count: count,
                    current: 0,
                    forwardEnabled: count > ghsState.slotSize,
                    backwardEnabled: false,
                };
                yield put(setGroupHighscoresPagingValues(params2));
                yield put(fetchGroupHighscore());
            }
        } finally {
            yield put(groupHighscoresSetIsLoading(false));
        }
    }
}

export function* fetchGroupHighscoreGenerator() {
    let state: GroupHighscoreStore = yield select(userGroupHighscoresSelector);
    const groupHighscoreApi = getGroupHighscoreApi2();

    const trId: string | undefined = yield select(userGroupHighscoresTrIdSelector);
    if (trId) {
        yield put(groupHighscoresSetIsLoading(true));
        try {
            yield* fetchWrapperFuncNoLoading(
                () =>
                    groupHighscoreApi.getGroupHighscores({
                        clientName: getSystemConfig().clientName,
                        trId: Number(trId),
                        startPos: state.current,
                        slotSize: state.slotSize,
                        q: state.searchString,
                    }),
                [(r) => setGroupHighscores(r)],
            );
        } finally {
            yield put(groupHighscoresSetIsLoading(false));
        }
    }
}

function* pageDownGroupHighscores() {
    let state: GroupHighscoreStore = yield select(userGroupHighscoresSelector);

    let newStart: number = state.current + state.slotSize;

    const trId: string | undefined = yield select(userGroupHighscoresTrIdSelector);

    if (trId) {
        if (newStart <= state.count) {
            let params: GroupHighscorePagingValues = {
                count: state.count,
                current: newStart,
                forwardEnabled: true,
                backwardEnabled: newStart < state.count,
            };
            yield put(setGroupHighscoresPagingValues(params));
            yield put(fetchGroupHighscore());
        }
    }
}

function* pageUpGroupHighscores() {
    let state: GroupHighscoreStore = yield select(userGroupHighscoresSelector);

    let newStart: number = state.current - state.slotSize;

    const trId: string | undefined = yield select(userGroupHighscoresTrIdSelector);

    if (trId) {
        if (newStart >= 0) {
            let params: GroupHighscorePagingValues = {
                count: state.count,
                current: newStart,
                forwardEnabled: newStart > 0,
                backwardEnabled: true,
            };
            yield put(setGroupHighscoresPagingValues(params));
            yield put(fetchGroupHighscore());
        }
    }
}

function* pageLastGroupHighscores() {
    let state: GroupHighscoreStore = yield select(userGroupHighscoresSelector);

    let newStart: number = getLastPageStart(state.count, state.slotSize);

    const trId: string | undefined = yield select(userGroupHighscoresTrIdSelector);

    if (trId) {
        if (newStart >= 0) {
            let params: GroupHighscorePagingValues = {
                count: state.count,
                current: newStart,
                forwardEnabled: false,
                backwardEnabled: true,
            };
            yield put(setGroupHighscoresPagingValues(params));
            yield put(fetchGroupHighscore());
        }
    }
}

function* pageFirstGroupHighscores() {
    let state: GroupHighscoreStore = yield select(userGroupHighscoresSelector);

    let newStart: number = 0;

    const trId: string | undefined = yield select(userGroupHighscoresTrIdSelector);

    if (trId) {
        let params: GroupHighscorePagingValues = {
            count: state.count,
            current: newStart,
            forwardEnabled: state.count > state.slotSize,
            backwardEnabled: false,
        };
        yield put(setGroupHighscoresPagingValues(params));
        yield put(fetchGroupHighscore());
    }
}

export function* tournamentRoundIdChanged() {
    // let ghsState = state.groupHighscore;
    // yield put(searchGroupHighscores(ghsState.searchString));
    // yield put(fetchGroupHighscore());
}

function* loadWithoutPropsGenerator() {
    yield* loadWithoutPropsTournamentRoundForwarder('highscores', userGroupHighscorePath);
}

function* loadHighscoresWithoutPropsGenerator() {
    yield* loadWithoutPropsTournamentRoundForwarder('highscores', highscoresPath);
}

export function* watchForPageUpGroupHighscores() {
    yield takeEvery(PAGE_UP_GROUP_HIGHSCORES, pageUpGroupHighscores);
}
export function* watchForPageDownGroupHighscores() {
    yield takeEvery(PAGE_DOWN_GROUP_HIGHSCORES, pageDownGroupHighscores);
}
export function* watchForPageFirstGroupHighscores() {
    yield takeEvery(PAGE_FIRST_GROUP_HIGHSCORES, pageFirstGroupHighscores);
}
export function* watchForPageLastGroupHighscores() {
    yield takeEvery(PAGE_LAST_GROUP_HIGHSCORES, pageLastGroupHighscores);
}

export function* watchForFetchGroupHighscores() {
    yield takeEvery(FETCH_GROUP_HIGHSCORES, fetchGroupHighscoreGenerator);
}

export function* watchForSearchGroupHighscores() {
    yield takeEvery(SEARCH_GROUP_HIGHSCORES, searchGroupHighscoreGenerator);
}

export function* watchForLoadWithoutProps() {
    yield takeEvery(GROUP_HIGHSCORES_LOAD_WITHOUT_PROPS, loadWithoutPropsGenerator);
    yield takeEvery(HIGHSCORES_LOAD_WITHOUT_PROPS, loadHighscoresWithoutPropsGenerator);
    yield takeEvery(GROUP_HIGHSCORES_LOAD_DEFAULT_ROUND, userGroupLoadDefaultHighscoreRoundGenerator);
}

export default function* groupHighscoreSaga() {
    yield all([
        watchForFetchGroupHighscores(),
        watchForSearchGroupHighscores(),
        watchForPageUpGroupHighscores(),
        watchForPageDownGroupHighscores(),
        watchForPageFirstGroupHighscores(),
        watchForPageLastGroupHighscores(),
        watchForLoadWithoutProps(),
    ]);
}
